/**
 * This file contains many functions that make various calls to the FN Catalog Item API and return the promises from those calls.
 */
import qs from 'qs';
import { range, flatMap } from 'lodash';
import Api from '../api.ts';

const CATALOG_ITEMS = '/catalog-items';

const CatalogItemAPI = {
  /**
   * Makes call to api endpoint to get all catalog items associated
   * with the current user's vendor
   * @param {string} params the URL params from the browser
   *
   * @returns {promise} promise returned from the api call
   * example: {
   *  catalogItems: [catalogItemObject1, catalogItemObject2, ...]
   * }
   */
  getCatalogItems(userId, params) {
    return Api.get(`/users/${userId}${CATALOG_ITEMS}?${qs.stringify(params)}`);
  },

  /**
   * Get purchase orders export
   *
   * @param userId
   *
   * @returns [promise] promise returned from api call that eventually
   * resolves to an array of purchase orders.
   *
   * example: purchaseOrders: [purchaseOrder1, purchaseOrder2, ...]
   */
  async getCatalogItemsExport(userId, params) {
    const limit = 100;
    const firstPage = await this.getCatalogItems(userId, {
      ...params,
      limit,
      page: 1,
    });
    const { count } = firstPage;
    const totalPages = Math.ceil(count / limit);

    const items = await Promise.all(
      range(1, totalPages).map(async (page) =>
        this.getCatalogItems(userId, {
          ...params,
          limit,
          page: page + 1,
        }),
      ),
    );
    items.unshift(firstPage);

    return flatMap(items, 'items');
  },

  /**
   * Makes a call to the API to update the vendor catalog item
   *
   * @param userId {integer}
   * @param catalogItemId {integer}
   * @param updates {object}
   */
  async updateCatalogItem(userId, catalogId, updates) {
    return Api.put(`/users/${userId}${CATALOG_ITEMS}/${catalogId}`, {
      data: updates,
    });
  },

  /**
   * Makes a call to the API to update multiple catalog item
   *
   * @param userId {integer}
   * @param catalogItemId {integer}
   * @param itemsUpdates {[object]}
   */
  async bulkUpdateCatalogItem(userId, itemsUpdates) {
    return Api.put(`/users/${userId}${CATALOG_ITEMS}`, {
      data: itemsUpdates,
    });
  },
};

export default CatalogItemAPI;
